/**
 * useSerialCursorScan: the ONE serial, client-driven cursor walk on the
 * product sync page (Phase 86, extracted from `useCoverageScan`).
 *
 * It owns the walk; the caller owns the fold. Callers pass how to fetch one
 * page (`fetchPage(cursor)`), how to start an accumulator (`createAcc`), how to
 * fold a page into it (`fold`) and how to turn it into the value the UI reads
 * (`finalize`, called after every page so the result is progressive).
 *
 * The properties that each cost the coverage scan a review round live here
 * once, so no second copy can drift:
 *
 * 1. **Serial, never parallel.** One page in flight at a time. Fanning out
 *    would multiply this org's Shopify call rate against a shared rate limit.
 *
 * 2. **Re-entering a tab must not re-spend the Shopify budget.** Callers fetch
 *    through `utils.<proc>.fetch(input, { staleTime: PAGE_STALE_MS })`. The
 *    explicit stale time is load-bearing: the app sets no default
 *    (`App.tsx:233-253`), so without it every cached page is stale and a tab
 *    flip re-walks Shopify from scratch. The fold itself is component state
 *    and is replayed on re-entry, which is cheap and deliberate.
 *
 * 3. **A superseded walk never folds.** The walk's identity is the caller's
 *    inputs plus an internal generation. A late page from an older identity
 *    (unmount, rescan, an input change) is dropped before it touches state.
 *
 * 4. **Complete only when the server says so.** Hitting `MAX_PAGES` stops the
 *    walk but leaves `isComplete` false: a partial answer must never claim to
 *    be the whole one.
 *
 * `rescan` calls the caller's `invalidate` and then bumps the generation.
 * Bumping alone would replay still-fresh cached pages and return the identical
 * answer: a Refresh button that does nothing.
 *
 * `usePapWorkScan` is deliberately NOT on this hook: it also merges live
 * per-product state updates and stops on a server budget dead-end, which would
 * need options no other caller wants.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** Hard stop on the cursor walk: 25,000 products at 50 a page, and a runaway-cursor backstop. */
export const MAX_PAGES = 500;

/**
 * How long a fetched page stays reusable. Matches the 5-minute window
 * `RipRoutingSection.tsx` uses for its own heavy paged scan. `rescan()`
 * invalidates, which bypasses it.
 */
export const PAGE_STALE_MS = 5 * 60 * 1000;

/** The cursor fields every walked page carries. */
export interface CursorPage {
  hasNextPage: boolean;
  endCursor: string | null | undefined;
}

export interface SerialCursorScanOptions<TPage extends CursorPage, TAcc, TResult> {
  /** Gate the walk, e.g. only while the tab that reads it is open. State is kept while disabled. */
  enabled: boolean;
  /**
   * The caller's inputs as a string. A change starts a fresh walk and drops
   * any page still in flight from the old one. The internal generation is
   * appended here; callers never pass it.
   */
  identity: string;
  /** Fetch one page. Callers go through the tRPC utils cache with `PAGE_STALE_MS`. */
  fetchPage: (cursor: string | null) => Promise<TPage>;
  /** Drop the caller's cached pages; `rescan` calls it before walking again. */
  invalidate: () => unknown;
  createAcc: () => TAcc;
  /** Fold one page into the accumulator (mutate it in place). */
  fold: (acc: TAcc, page: TPage) => void;
  /** Build what the UI reads. `morePages` is true while the walk will fetch another page. */
  finalize: (acc: TAcc, morePages: boolean) => TResult;
  /**
   * Keep the previous walk's result on screen until the new walk folds its
   * first page (a refetch that keeps the rows). Default false: the result
   * resets to null when a walk starts, as the coverage scan always has.
   */
  keepPreviousResult?: boolean;
}

export interface SerialCursorScanState<TResult> {
  /** Null until the current walk (or, with `keepPreviousResult`, any walk) folds a page. */
  result: TResult | null;
  /** Pages folded by the current walk. */
  pagesFolded: number;
  /** True once the server reports no further pages. */
  isComplete: boolean;
  /** True while a page request is in flight. */
  isScanning: boolean;
  /** Set when a page request failed; the walk stops and keeps what it folded. */
  error: string | null;
  /** Invalidate the cached pages and walk again from the first page. */
  rescan: () => void;
}

export function useSerialCursorScan<TPage extends CursorPage, TAcc, TResult>(
  options: SerialCursorScanOptions<TPage, TAcc, TResult>,
): SerialCursorScanState<TResult> {
  const { enabled, identity } = options;

  const [result, setResult] = useState<TResult | null>(null);
  const [pagesFolded, setPagesFolded] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generation, setGeneration] = useState(0);

  // The callbacks are fresh closures every render; the walk reads the latest
  // through a ref so they never restart it. Only `enabled` and the identity do.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Identity of the walk. A change here invalidates any in-flight run: a late
  // page from the previous identity must never fold into the new one.
  const scanKey = `${identity}::${generation}`;
  const activeKeyRef = useRef(scanKey);
  activeKeyRef.current = scanKey;

  const rescan = useCallback(() => {
    void optionsRef.current.invalidate();
    setGeneration((g) => g + 1);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    const myKey = scanKey;
    const superseded = () => cancelled || activeKeyRef.current !== myKey;

    const walk = async () => {
      const opts = optionsRef.current;
      const acc = opts.createAcc();
      let cursor: string | null = null;
      let pages = 0;

      setIsScanning(true);
      setError(null);
      setIsComplete(false);
      setPagesFolded(0);
      if (!opts.keepPreviousResult) setResult(null);

      try {
        for (;;) {
          const page = await optionsRef.current.fetchPage(cursor);

          // Bail before mutating state if this walk has been superseded.
          if (superseded()) return;

          optionsRef.current.fold(acc, page);
          pages += 1;

          const morePages = page.hasNextPage && page.endCursor != null && pages < MAX_PAGES;
          setResult(optionsRef.current.finalize(acc, morePages));
          setPagesFolded(pages);

          if (!morePages) {
            // Complete only when the server itself said there is nothing
            // left. Hitting MAX_PAGES is a partial answer.
            setIsComplete(!page.hasNextPage);
            return;
          }
          cursor = page.endCursor ?? null;
        }
      } catch (e) {
        if (superseded()) return;
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!superseded()) setIsScanning(false);
      }
    };

    void walk();
    return () => {
      cancelled = true;
    };
  }, [enabled, scanKey]);

  return { result, pagesFolded, isComplete, isScanning, error, rescan };
}
